import React, { useState } from 'react';
import useMountEffect from '@restart/hooks/useMountEffect';
import classNames from 'classnames/bind';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Button,
  DatePicker,
  message,
  Divider,
  Form,
  Input,
  InputNumber,
  Typography,
  Space,
} from 'antd';

import styles from './detail.module.scss';
import API from 'helpers/API';
import { BottomButtons, HeadingH1 } from 'components/ui';
import { getOrderStatus } from 'utils/CodeMapper';
import { getAddress } from 'utils/AddressFinder';
import { getDisplayName } from 'utils/getDisplayName';
import { formatSeoulDateTime } from 'utils/Formatter';
import { formatOrderDateValue, parseOrderDateValue } from './dateFields';

const cx = classNames.bind(styles);
const DELETE_ALLOWED_ROLES = ['SYSTEM', 'ADMIN'];

const Page = () => {
  const { rid } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState();
  const [form] = Form.useForm();
  const role = localStorage.getItem('@h2tms/role');

  useMountEffect(() => {
    // API: 주문 상세 조회
    API.get(`/orders/${rid}`).then(({ success, data }) => {
      if (success) {
        setOrder(data);
        form.setFieldsValue({
          ...data,
          orderDate: parseOrderDateValue(data.orderDate),
        });
      }
    });
  }, [rid]);

  const findAddress = () => {
    getAddress().then((data) => {
      form.setFieldValue('deliveryAddress', data.address);
    });
  };

  const doSave = (values) => {
    const data = {
      orderDate: formatOrderDateValue(values.orderDate),
      status: order.status,
      quantity: Number(values.quantity),
      customerName: values.customerName,
      customerCompany: values.customerCompany,
      customerPhone: values.customerPhone,
      deliveryName: values.deliveryName,
      deliveryCompany: values.deliveryCompany,
      deliveryPhone: values.deliveryPhone,
      deliveryAddress: values.deliveryAddress,
      deliveryAddressDetail: values.deliveryAddressDetail,
      memo: values.memo,
    };

    // API: 주문 수정
    API.patch(`/orders/${rid}`, data).then(({ success }) => {
      if (success) {
        message.success('저장을 완료했습니다.');
        navigate('/orders');
      } else {
        message.error('저장을 실패했습니다.');
      }
    });
  };

  const doRemove = () => {
    if (!DELETE_ALLOWED_ROLES.includes(role ?? '')) return;
    if (!window.confirm('정말 삭제하시겠습니까?')) return;

    API.delete(`/orders/${rid}`).then(({ success }) => {
      if (success) {
        message.success('삭제를 완료했습니다.');
        navigate('/orders');
      } else {
        message.error('삭제를 실패했습니다.');
      }
    });
  };

  return (
    <div className={cx('container')}>
      <Space direction="vertical" size={16} className={cx('wrapper')}>
        <HeadingH1 title={'주문 상세'} />
        <Form layout="vertical" onFinish={doSave} form={form} className={cx('form')}>
          <Typography.Text className={cx('title')} strong>
            주문정보
          </Typography.Text>
          <div className={cx('sectionPanel')}>
            <div className={cx('infoGridTwo')}>
              <Form.Item
                className={cx('formItemCompact')}
                name={'orderNo'}
                label="주문번호"
              >
                <Input disabled className={cx('fullWidthControl')} />
              </Form.Item>
              <Form.Item
                className={cx('formItemCompact')}
                name={'originNo'}
                label="원천 시스템 주문번호"
              >
                <Input disabled className={cx('fullWidthControl')} />
              </Form.Item>
            </div>
            <div className={cx('infoGridThree')}>
              <Form.Item
                className={cx('formItemCompact')}
                name={'orderDate'}
                label="주문일자"
                rules={[{ required: true, message: '주문일자를 선택해주세요' }]}>
                <DatePicker placeholder="주문일자 선택" className={cx('fullWidthControl')} />
              </Form.Item>
              <Form.Item
                className={cx('formItemCompact')}
                name={'quantity'}
                label="수량"
                rules={[{ required: true, message: '수량을 입력해주세요' }]}>
                <InputNumber className={cx('fullWidthControl')} />
              </Form.Item>
              <Form.Item className={cx('formItemCompact')} label="상태">
                <div className={cx('readonlyField')}>{getOrderStatus(order?.status) || '-'}</div>
              </Form.Item>
            </div>
            <div className={cx('infoGridThree')}>
              <Form.Item
                className={cx('formItemCompact')}
                name={'customerCompany'}
                label="주문자 회사명"
                rules={[{ required: true, message: '회사명을 입력해주세요' }]}>
                <Input className={cx('fullWidthControl')} />
              </Form.Item>
              <Form.Item
                className={cx('formItemCompact')}
                name={'customerName'}
                label="주문자명"
                rules={[{ required: true, message: '주문자명을 입력해주세요' }]}>
                <Input className={cx('fullWidthControl')} />
              </Form.Item>
              <Form.Item
                className={cx('formItemCompact')}
                name={'customerPhone'}
                label="주문자 전화번호"
                rules={[{ required: true, message: '전화번호를 입력해주세요' }]}>
                <Input className={cx('fullWidthControl')} />
              </Form.Item>
            </div>
          </div>
          <Divider />
          <Typography.Text className={cx('title')} strong>
            배송정보
          </Typography.Text>
          <div className={cx('sectionPanel')}>
            <div className={cx('infoGridThree')}>
              <Form.Item
                className={cx('formItemCompact')}
                name={'deliveryCompany'}
                label="수신자 회사명"
                rules={[{ required: true, message: '회사명을 입력해주세요' }]}>
                <Input className={cx('fullWidthControl')} />
              </Form.Item>
              <Form.Item
                className={cx('formItemCompact')}
                name={'deliveryName'}
                label="수신자명"
                rules={[{ required: true, message: '수신자명을 입력해주세요' }]}>
                <Input className={cx('fullWidthControl')} />
              </Form.Item>
              <Form.Item
                className={cx('formItemCompact')}
                name={'deliveryPhone'}
                label="수신자 전화번호"
                rules={[{ required: true, message: '전화번호를 입력해주세요' }]}>
                <Input className={cx('fullWidthControl')} />
              </Form.Item>
            </div>
            <div className={cx('infoGridTwoWide')}>
              <Form.Item
                className={cx('formItemCompact')}
                name={'deliveryAddress'}
                label="주소"
                rules={[{ required: true, message: '주소를 입력해주세요' }]}>
                <Input.Search onClick={findAddress} onSearch={findAddress} className={cx('fullWidthControl')} />
              </Form.Item>
              <Form.Item className={cx('formItemCompact')} name={'deliveryAddressDetail'} label="상세 주소">
                <Input className={cx('fullWidthControl')} />
              </Form.Item>
            </div>
          </div>
          <Divider />
          <Typography.Text className={cx('title')} strong>
            부가정보
          </Typography.Text>
          <div className={cx('additionalInfoGrid')}>
            <div className={cx('detailCard')}>
              <div className={cx('detailCardHeader')}>
                <Typography.Text strong>메모</Typography.Text>
              </div>
              <Form.Item name={'memo'} className={cx('memoField')}>
                <Input.TextArea className={cx('memoTextarea')} />
              </Form.Item>
            </div>
            <div className={cx('detailCard')}>
              <div className={cx('detailCardHeader')}>
                <Typography.Text strong>이력 요약</Typography.Text>
                <Typography.Text type="secondary">서울 시간 기준</Typography.Text>
              </div>
              <div className={cx('metaGrid')}>
                <div className={cx('metaCard')}>
                  <span className={cx('metaLabel')}>등록자</span>
                  <span className={cx('metaValue')}>{getDisplayName(order?.createdBy)}</span>
                </div>
                <div className={cx('metaCard')}>
                  <span className={cx('metaLabel')}>등록일시</span>
                  <span className={cx('metaValue', 'metaValueCompact')}>{formatSeoulDateTime(order?.createdAt)}</span>
                </div>
                <div className={cx('metaCard')}>
                  <span className={cx('metaLabel')}>수정자</span>
                  <span className={cx('metaValue')}>{getDisplayName(order?.updatedBy)}</span>
                </div>
                <div className={cx('metaCard')}>
                  <span className={cx('metaLabel')}>수정일시</span>
                  <span className={cx('metaValue', 'metaValueCompact')}>{formatSeoulDateTime(order?.updatedAt)}</span>
                </div>
              </div>
            </div>
          </div>

          <BottomButtons
            left={[
              <Button key={'backPage'} onClick={() => navigate(-1)}>
                뒤로
              </Button>,
              DELETE_ALLOWED_ROLES.includes(role ?? '') ? (
                <Button key={'removeOrder'} danger type="primary" onClick={doRemove}>
                  삭제
                </Button>
              ) : null,
            ]}
            right={[
              <Button key={'controlSerial'} type="primary" onClick={() => navigate(`/orders/${rid}/tanks`)}>
                용기 관리
              </Button>,
              <Button
                key={'controlDelivery'}
                type="primary"
                onClick={() => navigate(`/orders/${rid}/shipping`)}
                disabled={order?.orderTanks.length === 0}>
                배송 관리
              </Button>,
              <Button key={'saveOrder'} type="primary" htmlType="submit">
                저장
              </Button>,
            ]}
          />
        </Form>
      </Space>
    </div>
  );
};

export default Page;
